import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { getQueryString } from "../../common/utils"
import {
  ACTIVITY_LEGACY_ENDPOINT,
  FIND_OB_ORG_ENDPOINT,
  ORG_LIST_ENDPOINT,
  ORG_MANAGE_ENDPOINT,
  RTK_SLICE_API_LIST
} from "../../common/constants"
import {
  CreateOrgPayload,
  OrgActivity,
  Organization,
  Organizations,
  FindObOrgItem,
  ListPagination,
  OrgStatusFilter,
  OrgSortBy
} from "../../types"
import { baseQuery, getTags } from "../api"
import bbApi from "../../api"

const reducerPath = "org"
export const orgMembersPath = "org-members"

export const orgApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath, orgMembersPath],
  endpoints: builder => ({
    fetchOrgList: builder.query({
      query: (params: any) => `${ORG_LIST_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: { data: Organizations }) =>
        responseData?.data || [],
      providesTags: result => getTags(reducerPath, "id", result)
    }),
    // One page of orgs: the API pages when offset is present (limit clamped to
    // 200), searches by name and orders by title, answering with `pagination`.
    fetchOrgsPage: builder.query({
      query: (params: {
        limit: number
        offset: number
        search?: string
        status?: OrgStatusFilter
        sort_by?: OrgSortBy
        sort_dir?: "asc" | "desc"
      }) => `${ORG_LIST_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: {
        data: Organizations
        pagination?: ListPagination
      }) => ({
        data: responseData?.data || [],
        pagination: responseData?.pagination
      }),
      providesTags: result => getTags(reducerPath, "id", result?.data)
    }),
    fetchOrgDetails: builder.query({
      query: (params: { id: string }) =>
        `${ORG_MANAGE_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: Organization) => responseData || [],
      providesTags: (result, error, arg) => [{ type: reducerPath, id: arg.id }]
    }),
    fetchOrgActivities: builder.query({
      query: (params: {
        group_id: string
        org_id?: string
        range_start: number
        range_end: number
        limit?: number
        type?: string
      }) => `${ACTIVITY_LEGACY_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: OrgActivity[]) => responseData || []
    }),
    createOrganization: builder.mutation({
      queryFn: async (data: CreateOrgPayload) => {
        const response = await bbApi.org.create(data)
        return { data: response }
      },
      invalidatesTags: [{ type: reducerPath, id: RTK_SLICE_API_LIST }]
    }),
    updateOrganization: builder.mutation({
      queryFn: async (
        data: Partial<Organization> & Pick<Organization, "id">
      ) => {
        const response = await bbApi.org.update(data)
        return { data: response }
      },
      invalidatesTags: (result, error, arg) => [
        { type: reducerPath, id: arg.id }
      ]
    }),
    deleteOrganization: builder.mutation({
      queryFn: async (id: string) => {
        const response = await bbApi.org.delete(id)
        return { data: response }
      },
      invalidatesTags: (result, error, arg) => [
        { type: reducerPath, id: arg },
        { type: reducerPath, id: RTK_SLICE_API_LIST }
      ]
    }),
    findObOrg: builder.query({
      query: (params: {
        originator_id?: string
        biz_channel?: string
        org_id?: string
        name?: string
        ignore_cache?: boolean
      }) => `${FIND_OB_ORG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: FindObOrgItem[]) => responseData
    })
  })
})

export const {
  useFetchOrgListQuery,
  useFetchOrgsPageQuery,
  useFetchOrgDetailsQuery,
  useFetchOrgActivitiesQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useFindObOrgQuery
} = orgApi
